import { useUserAccount } from '@/api/user/queries';
import { useWalletsMe } from '@/api/wallets';
import { GenericError } from '@/models/generic-error';
import { UserRewardTracker } from '@/models/user-reward/tracker';
import { Decimal } from '@starsoft/common/services/Decimal';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { UserRewardsQueryKeys } from '../../queryKeys';
import {
  UserRewardsTrackerQueryKey,
  UseUserRewardsTrackerProps,
} from './props';
import { fetchUserRewardsTracker } from './service';

export function useUserRewardsTracker(options?: UseUserRewardsTrackerProps) {
  const { refetchOnMount = true } = options ?? {};
  const { isAuthorized } = useUserAccount();
  const { wallet } = useWalletsMe();

  const queryKey: UserRewardsTrackerQueryKey = [UserRewardsQueryKeys.Trackers];

  const { data, refetch, isPending, error, fetchStatus } = useQuery<
    Array<UserRewardTracker>,
    AxiosError<GenericError>,
    Array<UserRewardTracker>,
    UserRewardsTrackerQueryKey
  >({
    queryKey,
    queryFn: fetchUserRewardsTracker,
    enabled: isAuthorized,
    staleTime: 5 * 1000,
    refetchOnMount,
  });

  const totalAmountRedeemed: Decimal = useMemo(() => {
    return data?.reduce((acc, curr) => {
      return acc.sum([
        curr?.amount?.[wallet?.currentAccount?.coin?.code] ?? new Decimal(0),
      ]);
    }, new Decimal(0));
  }, [data]);

  return {
    error,
    refetch,
    isLoading: isPending && fetchStatus !== 'idle',
    trackers: data,
    totalAmountRedeemed,
  };
}
